import React from 'react';
import { Link } from 'react-router-dom';
import TestimonialCarousel from '../components/TestimonialCarousel';
import EarningsLadder from '../components/EarningsLadder';
import EarningsCalculator from '../components/EarningsCalculator';
import WhyJoin from '../components/WhyJoin';
import OnboardingPromo from '../components/OnboardingPromo';
import { useLanguage } from '../LanguageContext';
import { CONTENT } from '../content';

interface HomeProps {
  onOpenVoice: () => void;
}

export default function Home({ onOpenVoice }: HomeProps) {
  const { language } = useLanguage();
  const t = CONTENT[language];
  const home = t.home;

  return (
    <div className="flex flex-col">
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-suncoast-bg to-white pt-16 pb-20 overflow-hidden">
        <div className="absolute top-0 right-0 -mt-20 -mr-20 w-96 h-96 bg-suncoast-secondary opacity-10 rounded-full blur-3xl"></div>
        <div className="container mx-auto px-4 lg:px-12 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="max-w-2xl">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-suncoast-dark mb-6 leading-tight tracking-tight">
                {home.heroTitle} <br />
                <span className="text-suncoast-primary">{home.heroSubtitle}</span>
              </h1>
              <p className="text-lg md:text-xl text-gray-600 mb-8 leading-relaxed font-medium">
                {home.heroDesc}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Link 
                  to="/contact" 
                  className="px-8 py-4 bg-suncoast-accent text-white font-bold rounded-lg text-center shadow-lg hover:bg-orange-600 transition-transform transform hover:-translate-y-1"
                >
                  {t.common.bookPerformanceCheck}
                </Link>
                <button 
                  onClick={onOpenVoice}
                  className="px-8 py-4 bg-white border-2 border-suncoast-primary text-suncoast-primary font-bold rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
                >
                   <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" /></svg>
                   {t.common.talkToAI}
                </button>
              </div>
              <p className="text-sm text-gray-500 font-semibold flex items-center gap-2">
                 <span className="w-2 h-2 rounded-full bg-green-500"></span>
                 {home.supporting}
              </p>
            </div>
            
            <div className="hidden lg:block">
              <EarningsLadder />
            </div>
          </div>
        </div>
      </section>

      {/* 3 Pillars */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-12">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-suncoast-dark">{home.pillarsTitle}</h2>
            <p className="text-gray-600 mt-4 text-lg">{home.pillarsSubtitle}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {home.pillars.map((pillar, i) => (
              <div key={i} className="bg-suncoast-offWhite p-8 rounded-xl border border-gray-100 hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 bg-suncoast-primary/10 text-suncoast-primary rounded-full flex items-center justify-center text-2xl mb-6">
                  {pillar.icon}
                </div>
                <h3 className="text-xl font-bold mb-3">{pillar.title}</h3>
                <p className="text-gray-600 leading-relaxed">{pillar.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Calculator Section */}
      <section className="py-20 bg-gray-50 border-y border-gray-200">
        <div className="container mx-auto px-4 lg:px-12">
           <div className="text-center mb-12">
             <span className="text-suncoast-accent font-bold tracking-widest uppercase text-sm mb-2 block">{home.economicsLabel}</span>
             <h2 className="text-3xl font-bold text-suncoast-primary">{home.earningsConcept}</h2>
           </div>
           
           <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
             <div className="flex flex-col justify-center">
               <p className="text-gray-600 text-lg leading-relaxed mb-4">
                 {home.timeHorizons.p1.start} 
                 <strong className="text-suncoast-primary">{home.timeHorizons.p1.highlight}</strong>
                 {home.timeHorizons.p1.end}
               </p>
               <p className="text-gray-600 text-lg leading-relaxed mb-6">
                 {home.timeHorizons.p2.start}
                 <strong>{home.timeHorizons.p2.highlight1}</strong>
                 {home.timeHorizons.p2.mid}
                 <strong>{home.timeHorizons.p2.highlight2}</strong>
                 {home.timeHorizons.p2.end}
               </p>
               
               <OnboardingPromo />
             </div>
             <div>
               <EarningsCalculator />
             </div>
           </div>

           <WhyJoin />
        </div>
      </section>

      {/* What Changes Next Week */}
      <section className="py-20 bg-suncoast-primary text-white">
        <div className="container mx-auto px-4 lg:px-12">
           <div className="grid md:grid-cols-2 gap-12 items-center">
             <div>
               <h2 className="text-3xl font-bold mb-6">{home.whatChangesTitle}</h2>
               <p className="text-suncoast-bg text-lg mb-8 opacity-90">
                 We don't just give you a login; we change your workflow. Here is what happens when we partner:
               </p>
               <div className="grid gap-6">
                 {home.whatChangesItems.map((item, i) => (
                   <div key={i} className="flex gap-4">
                     <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0 font-bold border border-white/20">
                       {i + 1}
                     </div>
                     <div>
                       <h4 className="font-bold text-xl mb-1">{item.title}</h4>
                       <p className="text-suncoast-bg text-sm opacity-80">{item.desc}</p>
                     </div>
                   </div>
                 ))}
               </div>
             </div>
             
             <div className="bg-white/5 p-8 rounded-2xl border border-white/10 backdrop-blur-sm">
                <h3 className="text-xl font-bold mb-6 flex items-center gap-2">
                  <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                  {home.proofTitle}
                </h3>
                <p className="opacity-90 leading-relaxed mb-6">
                  {home.proofDesc}
                </p>
                <div className="p-4 bg-white/10 rounded-lg text-sm font-semibold text-center border border-white/20">
                   {home.credibilityLocations}
                </div>
             </div>
           </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-12">
          <h2 className="text-3xl font-bold text-center mb-12 text-suncoast-dark">{home.testimonialsTitle}</h2>
          <TestimonialCarousel testimonials={t.testimonials} />
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 bg-suncoast-offWhite">
        <div className="container mx-auto px-4 lg:px-12">
           <h2 className="text-3xl font-bold text-center mb-16 text-suncoast-dark">{home.timelineTitle}</h2>
           <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center relative">
             {/* Connector Line (Desktop) */}
             <div className="hidden md:block absolute top-8 left-0 w-full h-1 bg-gray-200 -z-10"></div>
             
             {home.timelineSteps.map((step, i) => (
               <div key={i} className="bg-white p-6 rounded-lg shadow-sm">
                 <div className="inline-block px-4 py-1 bg-suncoast-secondary text-white rounded-full text-sm font-bold mb-4">{step.day}</div>
                 <h3 className="text-xl font-bold mb-2">{step.title}</h3>
                 <p className="text-gray-600 text-sm">{step.desc}</p>
               </div>
             ))}
           </div>
           <div className="text-center mt-12">
             <Link to="/onboarding" className="text-suncoast-primary font-bold hover:underline">{home.timelineLink}</Link>
           </div>
        </div>
      </section>

      {/* CTA Footer */}
      <section className="py-24 bg-suncoast-secondary relative overflow-hidden">
        <div className="container mx-auto px-4 text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">{home.ctaTitle}</h2>
          <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto whitespace-pre-line">
            {home.ctaDesc}
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link 
              to="/reality-check" 
              className="px-8 py-4 bg-white text-suncoast-secondary font-bold rounded-lg shadow-lg hover:bg-gray-100"
            >
              {home.ctaButton}
            </Link>
            <Link 
              to="/contact" 
              className="px-8 py-4 bg-suncoast-accent text-white font-bold rounded-lg hover:bg-orange-600"
            >
              {home.contactButton}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}